import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import apiService from '../services/ApiService';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../contexts/ToastContext';
import PageHeader, { RefreshButton } from '../components/shell/PageHeader';
import UserToolsModal from '../components/users/UserToolsModal';
import BulkApprovalModal from '../components/users/BulkApprovalModal';
import {
    APPROVAL_BADGE, APPROVAL_LABEL, AUTH_PROVIDER_LABEL, PERSONA_LABEL,
    type AdminUserDetail, type AdminUserListItem, type ApprovalStatus,
} from '../types/users';
import { formatDate, formatDateTime, formatRelative, initialsOf } from '../utils/format';
import '../styles/users.scss';

/**
 * Scheda utente: chi e', a cosa ha accesso, come usa il portale e cosa ha
 * segnalato. Le azioni (approva, sospendi, ruolo, tool) sono le stesse
 * dell'elenco. Nessun dato di connessione agli ambienti: resta nel browser
 * dell'utente (vedi la decisione privacy in AdminController).
 */
const REPORT_STATUS: Record<string, string> = { Open: 'Aperta', InProgress: 'In lavorazione', Resolved: 'Risolta', Closed: 'Chiusa', Rejected: 'Respinta' };

const formatDuration = (sec?: number) => {
    if (!sec) return '';
    if (sec < 60) return `${sec}s`;
    const m = Math.round(sec / 60);
    return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;
};

const UserDetailPage: React.FC = () => {
    const { id } = useParams();
    const userId = Number(id);
    const navigate = useNavigate();
    const { user: me } = useAuth();
    const { showToast } = useToast();
    const [u, setU] = useState<AdminUserDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [toolsOpen, setToolsOpen] = useState<'edit' | 'approve' | null>(null);
    const [statusAction, setStatusAction] = useState<ApprovalStatus | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setU(await apiService.adminGetUserDetail<AdminUserDetail>(userId));
        } catch {
            showToast('Errore', 'Utente non trovato', 'danger');
            navigate('/users');
        } finally {
            setLoading(false);
        }
    }, [userId, navigate, showToast]);
    useEffect(() => { void load(); }, [load]);

    const totalOpens = u?.toolUsage.reduce((s, t) => s + t.count, 0) ?? 0;
    const activeDays = u?.dailyUsage.filter(d => d.count > 0).length ?? 0;

    const chart: ApexOptions = useMemo(() => ({
        chart: { type: 'bar', toolbar: { show: false }, sparkline: { enabled: false }, fontFamily: 'Inter, Segoe UI, sans-serif', foreColor: '#748096' },
        plotOptions: { bar: { borderRadius: 3, columnWidth: '62%' } },
        colors: ['#12616a'],
        dataLabels: { enabled: false },
        grid: { borderColor: '#edf0f5', strokeDashArray: 3, padding: { left: 4, right: 4 } },
        xaxis: {
            type: 'datetime',
            categories: (u?.dailyUsage ?? []).map(d => d.date),
            labels: { datetimeFormatter: { day: 'dd MMM' }, style: { fontSize: '10px' } },
            axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: { min: 0, forceNiceScale: true, labels: { formatter: (v: number) => `${Math.round(v)}` } },
        tooltip: { y: { formatter: (v: number) => `${v} aperture` } },
    }), [u]);

    const setRole = async (role: 'User' | 'Admin') => {
        if (!u) return;
        try {
            await apiService.adminSetUserRole(u.id, role);
            showToast('Ruolo aggiornato', role === 'Admin' ? 'Ora è amministratore' : 'Non è più amministratore', 'success');
            void load();
        } catch (e: any) {
            showToast('Errore', e?.response?.data?.error ?? 'Aggiornamento ruolo non riuscito', 'danger');
        }
    };

    const reactivate = async () => {
        if (!u) return;
        try {
            await apiService.adminSetUserApproval(u.id, 'Approved');
            showToast('Utente riattivato', u.email, 'success');
            void load();
        } catch (e: any) {
            showToast('Errore', e?.response?.data?.error ?? 'Operazione non riuscita', 'danger');
        }
    };

    if (loading && !u) return <div className="et-state py-5"><span className="spinner-border spinner-border-sm" /> Caricamento…</div>;
    if (!u) return null;

    const isMe = u.email === me?.email;
    const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ');
    const place = [u.city, u.country].filter(Boolean).join(', ');
    const listItem: AdminUserListItem = u;

    return (
        <>
            <Link to="/users" className="btn btn-sm btn-link p-0 mb-2"><i className="bi bi-arrow-left me-1" />Tutti gli utenti</Link>

            <PageHeader
                kicker="Scheda utente"
                leading={<span className={`user-avatar is-lg ${u.isOnline ? 'is-online' : ''}`}>{initialsOf(u.email, u.displayName)}</span>}
                title={<>{u.displayName || fullName || u.email}{isMe && <span className="badge bg-info ms-2 align-middle fs-6">tu</span>}</>}
                meta={<>
                    <span className={`badge ${APPROVAL_BADGE[u.approvalStatus]}`}>{APPROVAL_LABEL[u.approvalStatus]}</span>
                    {u.role === 'Admin' && <span className="badge bg-primary"><i className="bi bi-shield-lock me-1" />Amministratore</span>}
                    <span>{u.email}</span>
                    {u.isOnline
                        ? <span className="text-success fw-semibold"><i className="bi bi-circle-fill me-1" style={{ fontSize: 7 }} />Online ora</span>
                        : <span>Ultima attività {formatRelative(u.lastActivityAt ?? u.lastLoginAt)}</span>}
                </>}
                actions={<>
                    <RefreshButton onClick={load} loading={loading} />
                    {u.approvalStatus === 'Pending' && (
                        <>
                            <button className="btn btn-outline-danger btn-sm" onClick={() => setStatusAction('Rejected')}>
                                <i className="bi bi-x-circle me-1" />Rifiuta
                            </button>
                            <button className="btn btn-primary btn-sm" onClick={() => setToolsOpen('approve')}>
                                <i className="bi bi-check2-circle me-1" />Approva e assegna tool
                            </button>
                        </>
                    )}
                    {u.approvalStatus === 'Approved' && !isMe && (
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => setStatusAction('Suspended')}>
                            <i className="bi bi-pause-circle me-1" />Sospendi
                        </button>
                    )}
                    {(u.approvalStatus === 'Suspended' || u.approvalStatus === 'Rejected') && (
                        <button className="btn btn-primary btn-sm" onClick={() => void reactivate()}>
                            <i className="bi bi-arrow-counterclockwise me-1" />Riattiva
                        </button>
                    )}
                </>}
            />

            {u.approvalStatus === 'Rejected' && u.rejectedReason && (
                <div className="alert alert-danger"><strong>Motivo del rifiuto:</strong> {u.rejectedReason}</div>
            )}
            {!u.profileCompleted && (
                <div className="alert alert-warning py-2"><i className="bi bi-person-exclamation me-2" />Il profilo non è ancora completo: al prossimo accesso il portale chiederà di completarlo.</div>
            )}

            <div className="user-kpis">
                <div><strong>{totalOpens}</strong><span>aperture di tool · 30 giorni</span></div>
                <div><strong>{activeDays}</strong><span>giorni attivi · 30 giorni</span></div>
                <div><strong>{u.enabledToolCount ?? u.enabledTools.length}</strong><span>tool abilitati</span></div>
                <div><strong>{u.reports.length}</strong><span>segnalazioni inviate</span></div>
            </div>

            <div className="user-grid">
                <div className="user-grid__main">
                    <section className="card shadow-sm">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <span>Utilizzo negli ultimi 30 giorni</span>
                        </div>
                        <div className="card-body pt-2">
                            {totalOpens === 0
                                ? <div className="et-state">Nessun tool aperto negli ultimi 30 giorni.</div>
                                : <Chart options={chart} series={[{ name: 'Aperture', data: u.dailyUsage.map(d => d.count) }]} type="bar" height={180} />}
                            {u.toolUsage.length > 0 && (
                                <ul className="user-usage">
                                    {u.toolUsage.slice(0, 8).map(t => {
                                        const pct = Math.round((t.count / Math.max(...u.toolUsage.map(x => x.count))) * 100);
                                        return (
                                            <li key={t.toolId}>
                                                <Link to={`/tools/${t.toolId}`} className="user-usage__name">{t.toolName}</Link>
                                                <div className="user-usage__bar"><span style={{ width: `${pct}%` }} /></div>
                                                <span className="user-usage__count">{t.count}</span>
                                                <small title={formatDateTime(t.lastUsedAt)}>{formatRelative(t.lastUsedAt)}</small>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </section>

                    <section className="card shadow-sm">
                        <div className="card-header">Attività recente</div>
                        {u.recentActivity.length === 0
                            ? <div className="card-body"><div className="et-state">Nessuna attività registrata.</div></div>
                            : (
                                <ul className="list-group list-group-flush user-activity">
                                    {u.recentActivity.map((a, i) => (
                                        <li key={i} className="list-group-item">
                                            <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                                            <span className="flex-grow-1">Ha aperto <Link to={`/tools/${a.toolId}`}>{a.toolName}</Link>
                                                {a.durationSeconds ? <span className="text-body-secondary"> · {formatDuration(a.durationSeconds)}</span> : null}</span>
                                            <time title={formatDateTime(a.usedAt)}>{formatRelative(a.usedAt)}</time>
                                        </li>
                                    ))}
                                </ul>
                            )}
                    </section>

                    <section className="card shadow-sm">
                        <div className="card-header d-flex justify-content-between">
                            <span>Segnalazioni inviate</span>
                            <Link to="/quality/reports" className="small fw-normal">Tutte le segnalazioni</Link>
                        </div>
                        {u.reports.length === 0
                            ? <div className="card-body"><div className="et-state">Nessuna segnalazione.</div></div>
                            : (
                                <ul className="list-group list-group-flush">
                                    {u.reports.map(r => (
                                        <li key={r.id} className="list-group-item d-flex align-items-center gap-3">
                                            <span className={`badge ${r.type === 'Bug' ? 'bg-danger' : 'bg-info'}`}>{r.type}</span>
                                            <span className="flex-grow-1 min-w-0">
                                                <strong className="d-block text-truncate">{r.title}</strong>
                                                <small className="text-body-secondary">{r.toolName} · {formatDate(r.createdAt)}</small>
                                            </span>
                                            <span className="badge bg-light">{REPORT_STATUS[r.status] ?? r.status}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                    </section>
                </div>

                <aside className="user-grid__side">
                    <section className="card shadow-sm">
                        <div className="card-header">Profilo</div>
                        <div className="card-body">
                            <dl className="user-facts">
                                <dt>Nome</dt><dd>{fullName || u.displayName || '—'}</dd>
                                <dt>Azienda</dt><dd>{u.companyName || '—'}</dd>
                                <dt>Profilo d’uso</dt><dd>{PERSONA_LABEL[u.persona ?? ''] ?? u.persona ?? '—'}</dd>
                                <dt>Telefono</dt><dd>{u.phoneNumber || '—'}</dd>
                                <dt>Località</dt><dd>{place || '—'}</dd>
                                <dt>Lingua</dt><dd>{u.preferredLanguage?.toUpperCase() || '—'}</dd>
                                <dt>Accesso con</dt><dd>{AUTH_PROVIDER_LABEL[u.authProvider ?? ''] ?? u.authProvider ?? '—'}</dd>
                            </dl>
                        </div>
                    </section>

                    <section className="card shadow-sm">
                        <div className="card-header">Account</div>
                        <div className="card-body">
                            <dl className="user-facts">
                                <dt>Registrato</dt><dd>{formatDateTime(u.createdAt)}</dd>
                                <dt>Ultimo accesso</dt><dd>{formatDateTime(u.lastLoginAt)}</dd>
                                <dt>Approvato</dt><dd>{u.approvedAt ? <>{formatDate(u.approvedAt)}{u.approvedByEmail && <small className="d-block text-body-secondary">da {u.approvedByEmail}</small>}</> : '—'}</dd>
                                <dt>Comunicazioni</dt><dd>{u.messagesRead} lette{u.messagesTargeted ? ` · ${u.messagesTargeted} indirizzate solo a questo utente` : ''}</dd>
                            </dl>
                            <div className="d-flex flex-wrap gap-2 mt-3">
                                {u.role === 'User'
                                    ? <button className="btn btn-outline-secondary btn-sm" onClick={() => void setRole('Admin')}><i className="bi bi-shield-lock me-1" />Rendi amministratore</button>
                                    : !isMe && <button className="btn btn-outline-secondary btn-sm" onClick={() => void setRole('User')}><i className="bi bi-shield-x me-1" />Togli ruolo admin</button>}
                                <Link className="btn btn-outline-secondary btn-sm" to={`/messages/new?to=${u.id}`}><i className="bi bi-megaphone me-1" />Scrivi una comunicazione</Link>
                            </div>
                        </div>
                    </section>

                    <section className="card shadow-sm">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <span>Tool abilitati</span>
                            <button className="btn btn-link btn-sm p-0" onClick={() => setToolsOpen('edit')}>Modifica</button>
                        </div>
                        {u.enabledTools.length === 0
                            ? <div className="card-body small text-body-secondary">Nessun tool abilitato esplicitamente. Vede i tool pubblici adatti al suo profilo.</div>
                            : (
                                <ul className="list-group list-group-flush">
                                    {u.enabledTools.map(t => (
                                        <li key={t.toolId} className="list-group-item d-flex align-items-center gap-2 small">
                                            <Link to={`/tools/${t.toolId}`} className="flex-grow-1 text-body">{t.toolName}</Link>
                                            {t.visibility === 'Restricted' && <i className="bi bi-lock-fill text-warning-emphasis" title="Tool ristretto" />}
                                        </li>
                                    ))}
                                </ul>
                            )}
                    </section>
                </aside>
            </div>

            <UserToolsModal
                user={toolsOpen ? u : null}
                approve={toolsOpen === 'approve'}
                onClose={() => setToolsOpen(null)}
                onSaved={() => { setToolsOpen(null); void load(); }}
            />
            <BulkApprovalModal
                users={statusAction ? [listItem] : []}
                action={statusAction}
                onClose={() => setStatusAction(null)}
                onDone={() => { setStatusAction(null); void load(); }}
            />
        </>
    );
};

export default UserDetailPage;
